/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { Button } from "@mui/material"
import { ExclamationIcon } from "@heroicons/react/solid"
import Loading from "@medaica/common/components/loading"
import { logError } from "@medaica/common/services/util"
import { BookingAnswers, QuestionnaireDefinition } from "@medaica/common/types"
import { useBookingStores } from "../booking-context"
import WizardStepLayout from "../components/wizard-step-layout"
import QuestionnaireForm, { isComplete } from "../components/questionnaire-form"
import bookingCopy from "../bookingCopy"
import { bookingColors, footerButtonSx } from "../booking-theme"

const copy = bookingCopy.health

const Health = observer((): ReactElement => {
  const { sessionStore, uiStore, wizardStore } = useBookingStores()
  const patient = wizardStore.patient
  const patientName = patient ? `${patient.firstName} ${patient.lastName}`.trim() : ""
  const [definition, setDefinition] = useState<QuestionnaireDefinition | null>(null)
  const [answers, setAnswers] = useState<BookingAnswers>(wizardStore.answers ?? {})
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async (): Promise<void> => {
      try {
        const result = await sessionStore.runSessionCall(() => sessionStore.api.getQuestionnaire(), "health")
        // The real API answers {version: 0, questions: []} when no definition is active
        // (M4 §A5 default) — treat that as "unavailable" (the step stays skippable).
        if (result && result.questions.length > 0) {
          setDefinition(result)
        }
      } catch (error) {
        logError(error)
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [sessionStore])

  // Mandatory: every visible, non-optional question must be answered before continuing.
  // Uses visibleQuestions so show_if is respected (a future conditional version still works);
  // the "unavailable" path (no definition) has nothing to enforce.
  const complete = !definition || isComplete(definition, answers)

  const continueToTime = (): void => {
    if (!complete) {
      return
    }
    // Persist answers only when there's a questionnaire; the "unavailable" path leaves them unset.
    if (definition) {
      wizardStore.setAnswers(answers)
    }
    uiStore.goTo("time")
  }

  let body: ReactElement
  if (loading) {
    body = <Loading />
  } else if (definition) {
    body = (
      <>
        {definition.intro && definition.intro.length > 0 ? (
          // Intro lead-in: indigo left accent bar + a hairline divider below, so it reads as
          // context and is clearly separated from the first question. Colors inline (guaranteed).
          <div
            className="mb-6 space-y-3 pb-6 pl-4 text-gray-600"
            style={{
              borderLeftWidth: 3,
              borderLeftColor: bookingColors.primary,
              borderBottomWidth: 1,
              borderBottomColor: "#eef0f2",
            }}
          >
            {definition.intro.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
          </div>
        ) : null}
        <QuestionnaireForm definition={definition} answers={answers} onChange={setAnswers} />
      </>
    )
  } else {
    body = <p className="text-sm text-gray-500">{copy.unavailable}</p>
  }

  return (
    <WizardStepLayout current="health" headerRight={bookingCopy.bookingFor(patientName)}>
      <div className="mb-6">
        <h1 className="text-2xl font-bold" style={{ color: bookingColors.primary }}>
          {copy.title}
        </h1>
        <p className="mt-1 text-gray-600">{copy.subtitle}</p>
        {definition && !complete ? <p className="mt-2 text-sm text-gray-500">{copy.completePrompt}</p> : null}
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">{body}</div>

      {/* Global safety notice — hardcoded label copy (bookingCopy), never from the questionnaire API.
          Softer amber + a warning icon so it reads as an intentional, calm "important" callout. Amber
          via inline style — the Tailwind amber-* classes don't reliably generate in this build. */}
      <div
        className="mt-4 flex items-start gap-3 rounded-lg border px-4 py-3 text-sm"
        style={{ backgroundColor: "#FFFBEB", borderColor: "#FDE68A", color: "#78350F" }}
      >
        <ExclamationIcon className="mt-0.5 h-5 w-5 flex-shrink-0" style={{ color: "#D97706" }} aria-hidden="true" />
        <p>
          <strong className="font-semibold">{copy.emergencyNoticeLabel} —</strong> {copy.emergencyNotice}
        </p>
      </div>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outlined" color="inherit" sx={footerButtonSx} onClick={() => uiStore.goTo("consent")}>
          {copy.back}
        </Button>
        <Button variant="contained" sx={footerButtonSx} disabled={!complete} onClick={continueToTime}>
          {copy.continueButton}
        </Button>
      </div>
    </WizardStepLayout>
  )
})

export default Health
